import { findFilterLabelFromCode } from 'components/SelectedFilters/utils';
import { format } from 'date-fns';
import { DEPARTURE_PORT, DESTINATION, SHIP } from '../constants';
import { getFilterOrdered } from './filterParser';
import { getLabelsByBrand } from './brandsUtils';
import { IMAGE_AEM_CLOUD_PREFIX } from 'lib/constants';

const META_TAGS = {
  metaData: 'metadata',
  robots: 'Robots',
  content: 'Content',
  siteName: 'SiteName',
  ogSiteName: 'OgSiteName',
  phoneNumber: 'PhoneNumber',
  nights: 'Nights',
  singleNight: 'SingleNight',
  titleFormat: 'TitleFormat',
  titleFormatYear: 'TitleFormatYear',
  title: 'Title',
  descriptionFormat: 'DescriptionFormat',
  subtitle: 'Subtitle',
  subtitleFormat: 'SubtitleFormat',
  description: 'Description',
};

const rciLabels = {
  metadataImage:
    '/content/dam/royal/ships/oasis/oasis-of-the-seas-labadee-haiti-aft.jpg',
  metadataImageSize: '?$1440x600$',
};

const replaceLabels = (text, labelObj) => {
  let newText = text;
  let regex = '';
  Object.keys(labelObj).forEach(label => {
    regex = new RegExp(`{{${label}}}`, 'g');
    newText = newText.replace(regex, labelObj[label]);
  });
  return newText;
};

const metadataFriendlyValue = (values, friendly, moreFriendly) => {
  const filterValues = values.sort((a, b) => (a > b ? 1 : -1));
  let resultText = '';

  if (filterValues.length > 0) {
    const baseText = filterValues.length < 3 ? friendly : moreFriendly;
    resultText = baseText.replace(
      '{{value}}',
      `${filterValues
        .slice(0, 2)
        .join(filterValues.length < 3 ? ' & ' : ', ')}`,
    );
  }
  return resultText;
};

const lowerCaseWords = (text, words) => {
  let newText = text;
  let regex = '';
  words.map(word => {
    regex = new RegExp(`${word}`, 'g');
    newText = newText.replace(regex, match => match.toLowerCase());
  });
  return newText;
};

const capitalizeWords = (text, words) => {
  let newText = text;
  let regex = '';
  words.map(word => {
    regex = new RegExp(`${word}`, 'gi');
    newText = newText.replace(regex, word);
  });
  return newText;
};

const replaceMetadataValues = (inputObj, country, config, locale, cruiseSearchDictionary) => {
  const { baseUrl, brand } = config;

  const labels = getLabelsByBrand(brand.code);
  const getLabel = label =>
    labels[`${META_TAGS.metaData}${label}${country}`] ||
    labels[`${META_TAGS.metaData}${label}`];

  const getOgSiteName = (label) => {
    const rciOgLabel = labels[`${META_TAGS.metaData}${label}`].includes('{{countryName}}')
    if (rciOgLabel) return labels[`${META_TAGS.metaData}${label}`].replace('{{countryName}}', locale?.nameCountry)
    else return labels[`${META_TAGS.metaData}${label}`]
  }

  let metaData = {
    title: '',
    description: '',
    robots: replaceLabels(getLabel(META_TAGS.robots), {
      negation: inputObj.onFollowFilters ? '' : 'no',
    }),

    content: getLabel(META_TAGS.content),
    siteName: getLabel(META_TAGS.siteName),
    ogSiteName: cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataSiteName'] ?? getOgSiteName(META_TAGS.ogSiteName),
    phoneNumber: getLabel(META_TAGS.phoneNumber),
    canonical: getCanonical(baseUrl, inputObj?.path, inputObj?.sortedFilters),
  };

  const currentYear = parseInt(format(new Date(), 'yyyy'), 10);
  const nextYear = currentYear + 1;

  let nights = '';
  const metadataDescriptionLowercase = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataDescriptionLowercase'] ?? labels.metadataDescriptionLowercase;

  if (
    inputObj.nights.length ||
    inputObj.destinations.length ||
    inputObj.departures.length ||
    inputObj.ships.length
  ) {
    if (inputObj.nights.length) {
      const totalNights = inputObj.nights.reduce(
        (prev, curr) =>
          prev.concat(
            curr
              .replace(/(gte)(\d+)/, '$2+')
              .replace(/(lte)(\d+)/, '$2-')
              .split('~'),
          ),
        [],
      );

      if (totalNights.length === 1) {
        nights = replaceLabels(getLabel(META_TAGS.singleNight), {
          low: totalNights[0],
        });
      } else {
        nights = replaceLabels(getLabel(META_TAGS.nights), {
          low: totalNights[0],
          high: totalNights[totalNights.length - 1],
        });
      }
    }

    const metadataValues = {
      destinations: '',
      departures: '',
      ships: '',
    };

    const metadataTitleFormat = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataTitleFormat'] ?? getLabel(META_TAGS.titleFormat);
    const metadataTitleFormatYear = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataTitleFormatYear'] ?? getLabel(META_TAGS.titleFormatYear);
    const metadataDescriptionFormat = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataDescriptionFormat'] ?? getLabel(META_TAGS.descriptionFormat);
    const metadataSubtitle = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataSubtitle'] ?? getLabel(META_TAGS.subtitleFormat);

    Object.keys(metadataValues).forEach(key => {
      const labelKey = key.charAt(0).toUpperCase() + key.slice(1);
      metadataValues[key] = metadataFriendlyValue(
        inputObj[key],
        getLabel(labelKey),
        getLabel(`More${labelKey}`),
      );
    });

    metaData.title = [
      replaceLabels(metadataTitleFormat, {
        nights,
        ...metadataValues,
      }).trim(),
      replaceLabels(metadataTitleFormatYear, {
        currentYear,
        nextYear,
      }),
    ].join(' | ');

    metaData.description = capitalizeWords(
      lowerCaseWords(
        [
          replaceLabels(metadataDescriptionFormat, {
            nights,
            ...metadataValues,
          }).trim(),
          replaceLabels(metadataSubtitle, {
            nights,
            ...metadataValues,
          }).trim(),
        ].join('. '),
        metadataDescriptionLowercase.split(','),
      ),
      labels.metadataDescriptionTitlecase.split(','),
    );
  } else {
    const metadataTitle = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataTitle'] ?? getLabel(META_TAGS.title);
    const metadataDescription = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataDescription'] ?? getLabel(META_TAGS.description);
    const metadataSubtitle = cruiseSearchDictionary?.['cruiseSearch.general.labelMetadataSubtitle'] ?? getLabel(META_TAGS.subtitle);

    metaData.title = replaceLabels(metadataTitle, {
      currentYear,
      nextYear,
    });
    metaData.description = capitalizeWords(
      lowerCaseWords(
        `${metadataDescription}${metadataSubtitle}`,
        metadataDescriptionLowercase.split(','),
      ),
      labels.metadataDescriptionTitlecase.split(','),
    );
  }

  return metaData;
};

const getMetadataValues = (
  { search: searchApplied, ...aditionalFilters },
  country,
  filterOptions,
  path,
  config,
  locale,
  cruiseSearchDictionary
) => {
  const getLabelsForFilters = (filters, filterKey) => {
    return filters
      ? filters
          .map(x => findFilterLabelFromCode(x, null, filterOptions[filterKey]))
          .filter(text => !!text)
      : [];
  };

  return replaceMetadataValues(
    {
      nights: searchApplied.nights ? searchApplied.nights : [],
      destinations: getLabelsForFilters(searchApplied.destination, DESTINATION),
      departures: getLabelsForFilters(
        searchApplied.departurePort,
        DEPARTURE_PORT,
      ).map(port => port?.split(',')[0]),
      onFollowFilters:
        (!Object.keys(searchApplied).length &&
          !aditionalFilters?.sort?.length &&
          !aditionalFilters?.qualifiers.length) ||
        (!!Object.keys(searchApplied).length &&
          !!(
            searchApplied.nights ||
            searchApplied.destination ||
            searchApplied.departurePort ||
            searchApplied.ship
          )),
      ships: capitalizeShipName(getLabelsForFilters(searchApplied.ship, SHIP)),
      sortedFilters: getFilterOrdered(searchApplied),
      path,
    },
    country,
    config,
    locale,
    cruiseSearchDictionary
  );
};

const capitalizeShipName = labels => {
  if (!labels || labels.length === 0) {
    return labels;
  }

  return labels.map(label =>
    label
      .split(' ')
      .map((word, i, arr) =>
        i === 0 || i === arr.length - 1
          ? word?.charAt(0).toUpperCase() + word?.slice(1).toLowerCase()
          : word?.toLowerCase(),
      )
      .join(' '),
  );
};

const getMetadataImage = (cruises, config) => {
  const { imageBaseUrl } = config;
  let image = rciLabels.metadataImage;

  if (cruises.length) {
    const images = cruises[0].masterSailing?.itinerary?.media?.images;
    image = (images?.[0]?.path || rciLabels.metadataImage).replace(IMAGE_AEM_CLOUD_PREFIX, '');
  }

  return `${imageBaseUrl}${image}${rciLabels.metadataImageSize}`;
};

const getCanonical = (url, _path, filter = {}) => {
  const path = _path.split("?")[0];
  const { destination, departurePort, ship, nights } = filter;
  
  if (destination?.length)
    return `${url}${path}?search=destination:${destination[0]}`;

  if (departurePort?.length)
    return `${url}${path}?search=departurePort:${departurePort[0]}`;

  if (ship?.length)
    return `${url}${path}?search=ship:${ship[0]}`;

  if (nights)
    return `${url}${path}?search=nights:${nights}`;
  
  return `${url}${path}`;
};

export {
  replaceLabels,
  metadataFriendlyValue,
  getMetadataValues,
  getMetadataImage,
  replaceMetadataValues,
  getCanonical,
  lowerCaseWords,
  capitalizeWords,
  capitalizeShipName,
};
